import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, ZapIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';

import { WorkflowConfigurationMap, WorkflowScopeType } from '@invenco/common-domain/enums';
import { RuleDTO, WorkflowDTO } from '@invenco/common-interface/automation';

import { Button } from '@/components/core/button';
import { Form } from '@/components/core/form';
import { Skeleton } from '@/components/core/skeleton';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { workflowStatusType } from '@/shared/status-maps';
import { workflowStatusTitle } from '@/shared/title-maps';

import {
  WorkflowFormFields,
  formValuesToWorkflowPatch,
  workflowFormSchema,
  workflowToFormValues,
  type WorkflowFormValues,
} from '../workflow-form-fields';
import { useWorkflow } from './use-workflow';
import { useWorkflowEditorState, WorkflowEditorInput } from './use-workflow-editor-state';
import { WorkflowActions } from './workflow-actions';
import { WorkflowCanvas } from './workflow-canvas';
import { WorkflowEditorSide } from './workflow-editor-side';

type Props = {
  scopeType: WorkflowScopeType;
};

/** Stable empty seed so the create editor doesn't re-seed its rules on every render. */
const NO_RULES: RuleDTO[] = [];

/** `scopeId` is required only for WMS scope, where the user picks an instance. */
function buildCreateSchema(scopeType: WorkflowScopeType) {
  return workflowFormSchema.superRefine((values, ctx) => {
    if (scopeType === WorkflowScopeType.WMS_INSTANCE && !values.scopeId) {
      ctx.addIssue({ code: 'custom', path: ['scopeId'], message: 'Select a WMS instance' });
    }
  });
}

/** Create-mode form seed; dataType/triggerType are the scope's config defaults. */
function blankFormValues(input: WorkflowEditorInput): WorkflowFormValues {
  return {
    name: '',
    description: '',
    dataType: input.dataType,
    triggerType: input.triggerType,
    active: true,
    scopeId: '',
  };
}

/** Account workflows omit the scope fields (the API derives them); WMS carries both. */
function createPayload(
  values: WorkflowFormValues,
  scopeType: WorkflowScopeType,
  rules: RuleDTO[],
): Partial<WorkflowDTO> {
  return {
    ...formValuesToWorkflowPatch(values),
    rules,
    ...(scopeType === WorkflowScopeType.WMS_INSTANCE ? { scopeType, scopeId: values.scopeId } : {}),
  };
}

/**
 * The workflow editor page — both `workflows-shad/new` and `workflows-shad/:id`.
 * Deliberately one page, diverging from the documented create/edit split: the
 * entity is shallow and the two modes barely differ. The `:id` param picks the
 * mode, changing only hook inputs and header chrome; every hook runs in both
 * modes (`useWorkflow`'s fetch is disabled without an id).
 */
export function WorkflowEditor({ scopeType }: Props) {
  const { id } = useParams();
  const isNew = id === undefined;

  const navigate = useNavigate();
  const { automationGateway } = useGateways();
  const { workflow, isLoading, updateWorkflow, deleteWorkflow } = useWorkflow(id);

  const { mutate: createWorkflow } = useGatewayMutation<WorkflowDTO, Partial<WorkflowDTO>>({
    mutationFn: (payload) => automationGateway.createWorkflow(payload),
    onSuccess: (created) => {
      void navigate(`/automation/workflows-shad/${created.id}`);
    },
    linkedQuery: ['workflows', undefined],
    successMessage: 'Created workflow',
  });

  const editorInput = useMemo<WorkflowEditorInput>(() => {
    const { dataTypes, triggerTypes } = WorkflowConfigurationMap[scopeType];
    return {
      dataType: workflow?.dataType ?? dataTypes[0],
      triggerType: workflow?.triggerType ?? triggerTypes[0],
      rules: workflow?.rules ?? NO_RULES,
    };
  }, [workflow, scopeType]);

  const formValues = useMemo(
    () => (workflow ? workflowToFormValues(workflow) : blankFormValues(editorInput)),
    [workflow, editorInput],
  );
  const form = useForm<WorkflowFormValues>({
    resolver: zodResolver(isNew ? buildCreateSchema(scopeType) : workflowFormSchema),
    // RHF re-syncs the form when `values` change (the async detail load);
    // `keepDirtyValues` stops a refetch clobbering unsaved edits.
    values: formValues,
    resetOptions: { keepDirtyValues: true },
  });

  const onSave = useCallback(
    (values: WorkflowFormValues, rules: RuleDTO[]): Promise<Result> => {
      if (isNew) {
        return createWorkflow(createPayload(values, scopeType, rules));
      }
      if (!workflow) {
        return Promise.resolve(Result.fail('Cannot save before the workflow has loaded'));
      }
      return updateWorkflow({ ...formValuesToWorkflowPatch(values), rules });
    },
    [isNew, createWorkflow, scopeType, workflow, updateWorkflow],
  );

  const editor = useWorkflowEditorState({
    workflow: editorInput,
    scopeType,
    form,
    onSave,
  });

  const detailLoading = !isNew && isLoading;

  return (
    <Page>
      <Form {...form}>
        <Header loading={detailLoading}>
          {isNew ? (
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="row-start-1 -ml-2 justify-self-start"
            >
              <Link to="/automation/workflows" aria-label="Close">
                <XIcon className="size-5" />
              </Link>
            </Button>
          ) : (
            <HeaderIcon parentUrl="/automation/workflows" parentTitle="Workflows">
              <ZapIcon />
            </HeaderIcon>
          )}
          <HeaderTitle
            pageTitle={isNew ? 'New workflow' : workflow?.name}
            className={isNew ? undefined : 'flex flex-wrap items-center gap-2'}
          >
            {isNew ? (
              'New workflow'
            ) : (
              <>
                {workflow?.name}
                {workflow?.status ? (
                  <StatusBadge
                    status={workflow.status}
                    type={workflowStatusType}
                    title={workflowStatusTitle}
                  />
                ) : null}
              </>
            )}
          </HeaderTitle>
          <WorkflowActions
            editor={editor}
            workflow={workflow}
            isNew={isNew}
            loading={detailLoading}
            deleteWorkflow={deleteWorkflow}
          />
        </Header>
        {/* On narrow viewports the side slot stacks above the canvas. */}
        <PageContent variant="split">
          <div className="order-2 min-h-0 min-w-0 flex-1 @2xl:order-none">
            {/* Gate the canvas on load so the placeholder rule an unloaded
                workflow materializes can't be selected and edited. */}
            {detailLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <WorkflowCanvas editor={editor} />
            )}
          </div>
          <WorkflowEditorSide editor={editor}>
            {detailLoading ? (
              <Skeleton className="h-72 w-full rounded-xl" />
            ) : (
              // A real <form> gives Enter-to-submit; the header Save sits outside it.
              <form onSubmit={editor.submit}>
                <WorkflowFormFields scopeType={scopeType} isNew={isNew} />
              </form>
            )}
          </WorkflowEditorSide>
        </PageContent>
      </Form>
    </Page>
  );
}
